import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  invoicesService,
  paymentsService,
  projectsService,
} from '../../services/supabase';
import { Payment, Project } from '../../types';
import { ENABLE_INVOICES } from '../../config/featureFlags';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { useTranslations } from '../../hooks/useTranslations';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import Card, { CardContent } from '../../components/ui/Card';
import IssueInvoiceForm, {
  IssueInvoiceCreatedPayload,
} from '../../components/payments/IssueInvoiceForm';
import { PAGE_STACK } from '../../styles/pageLayout';
import { downloadPaymentInvoicePdf } from '../../utils/invoiceDownload';
import { isOptimisticId } from '../../utils/optimisticId';

const IssueInvoice: React.FC = () => {
  const { t } = useI18n();
  const { showToast } = useToast();
  const { formatCurrency, formatDateShort } = useTranslations();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('paymentId');

  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const goBackToFinances = () => {
    navigate('/app/finances');
  };

  useEffect(() => {
    if (!ENABLE_INVOICES) {
      navigate('/app/finances', { replace: true });
      return;
    }

    if (!paymentId || isOptimisticId(paymentId)) {
      setLoadError(t('invoices.paymentMissing'));
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [pay, existingInvoice] = await Promise.all([
          paymentsService.getById(paymentId),
          invoicesService.getByPaymentId(paymentId),
        ]);

        if (cancelled) return;

        if (!pay) {
          setLoadError(t('invoices.paymentMissing'));
          setLoading(false);
          return;
        }

        if (existingInvoice) {
          showToast(t('invoices.alreadyExists'), 'info');
          navigate('/app/finances', { replace: true });
          return;
        }

        setPayment(pay);

        const proj = pay.projectId ? await projectsService.getById(pay.projectId) : null;
        if (cancelled) return;
        setProject(proj);
      } catch (e) {
        console.error('Error loading invoice issue page:', e);
        if (!cancelled) setLoadError(t('invoices.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [paymentId, navigate, showToast, t]);

  const handleCreated = ({
    invoice,
    seller,
    buyer,
    lineItems,
    currency,
    dueDate,
    invoiceName,
    paymentTerms,
    paymentMethod,
    iban,
    bic,
    accountHolder,
  }: IssueInvoiceCreatedPayload) => {
    if (!payment) return;
    showToast(t('invoices.created'), 'success');
    const paymentMethodLabel =
      paymentMethod === 'paypal'
        ? t('invoices.paymentMethodPaypal')
        : paymentMethod === 'check'
          ? t('invoices.paymentMethodCheck')
          : paymentMethod === 'other'
            ? t('invoices.paymentMethodOther')
            : t('invoices.paymentMethodTransfer');
    downloadPaymentInvoicePdf({
      payment: { ...payment, dueDate },
      invoice,
      seller,
      buyer,
      lineItems,
      currency,
      invoiceName,
      paymentTerms,
      paymentMethodLabel,
      accountHolder,
      iban,
      bic,
      projectName: project?.name,
      formatDateShort,
      formatCurrency,
      t,
    });
    navigate('/app/finances', { replace: true });
  };

  return (
    <div className={`w-full ${PAGE_STACK}`}>
      {loading ? (
        <>
          <div className="flex items-start gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={goBackToFinances}
              className="mt-0.5 shrink-0 !px-2"
              aria-label={t('common.back')}
              icon={<ArrowLeft className="h-4 w-4" />}
            />
            <div className="min-w-0 flex-1">
              <PageHeader title={t('invoices.createTitle')} />
            </div>
          </div>
          <div className="flex justify-center py-16">
            <Spinner size="lg" label={t('common.loading')} />
          </div>
        </>
      ) : loadError || !payment ? (
        <>
          <div className="flex items-start gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={goBackToFinances}
              className="mt-0.5 shrink-0 !px-2"
              aria-label={t('common.back')}
              icon={<ArrowLeft className="h-4 w-4" />}
            />
            <div className="min-w-0 flex-1">
              <PageHeader title={t('invoices.createTitle')} />
            </div>
          </div>
          <Card>
            <CardContent className="py-10 text-center space-y-4">
              <p className="text-sm text-gray-600">{loadError || t('invoices.paymentMissing')}</p>
              <Button type="button" variant="primary" size="md" onClick={goBackToFinances}>
                {t('invoices.backToFinances')}
              </Button>
            </CardContent>
          </Card>
        </>
      ) : (
        <IssueInvoiceForm
          payment={payment}
          project={project}
          onCancel={goBackToFinances}
          onCreated={handleCreated}
          leading={
            <div className="flex items-start gap-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={goBackToFinances}
                className="mt-0.5 shrink-0 !px-2"
                aria-label={t('common.back')}
                icon={<ArrowLeft className="h-4 w-4" />}
              />
              <div className="min-w-0 flex-1">
                <PageHeader title={t('invoices.createTitle')} />
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default IssueInvoice;
